import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionScopeDetail } from './hooks/useScopes';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  pending_review: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-600',
  locked: 'bg-slate-900 text-white',
  revision_required: 'bg-amber-100 text-amber-600',
  superseded: 'bg-slate-400 text-white',
  cancelled: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  pending_review: 'Chờ duyệt',
  approved: 'Đã duyệt',
  locked: 'Đã khóa',
  revision_required: 'Cần chỉnh sửa',
  superseded: 'Đã thay thế',
  cancelled: 'Đã hủy',
};

export default function ScopeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { scope: item, items, isLoading, updateScope } = useConstructionScopeDetail(id);

  const [activeTab, setActiveTab] = useState('items');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết phạm vi...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin phạm vi thi công</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateScope({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'items', label: 'Hạng mục chi tiết' },
    { value: 'zones', label: 'Khu vực & Giai đoạn' },
    { value: 'boq', label: 'Liên kết BOQ' },
    { value: 'documents', label: 'Tài liệu kỹ thuật' },
    { value: 'versions', label: 'Phiên bản' },
  ];

  return (
    <PageDetailLayout
      title={item.scope_name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.scope_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Phạm vi thi công', path: '/construction/scopes' },
        { label: item.scope_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'pending_review' && (
            <AuthButton module="construction" action="approve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('approved')}>
              Phê duyệt Phạm vi
            </AuthButton>
          )}
          {item.status_code === 'approved' && (
            <AuthButton module="construction" action="lock" variant="primary" icon="ri-lock-line" onClick={() => handleStatusChange('locked')}>
              Khóa phiên bản
            </AuthButton>
          )}
          <AuthButton module="construction" action="update" variant="outline" icon="ri-edit-line">
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Mô tả phạm vi
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  "{item.description || 'Chưa có mô tả chi tiết cho phạm vi này.'}"
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-6">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Mã hợp đồng</span>
                    <span className="text-sm font-bold text-os-primary underline">{item.contract?.contract_code || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người lập</span>
                    <span className="text-sm font-bold text-slate-700">QS Manager</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày bắt đầu dự kiến</span>
                    <span className="text-sm font-bold text-slate-700">{item.start_date ? new Date(item.start_date).toLocaleDateString('vi-VN') : '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày hoàn thành dự kiến</span>
                    <span className="text-sm font-bold text-slate-700">{item.end_date ? new Date(item.end_date).toLocaleDateString('vi-VN') : '---'}</span>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <KPICard title="Tổng hạng mục" value={items.length} icon="ri-list-settings-line" variant="primary" />
                <KPICard title="Khu vực thi công" value={3} icon="ri-map-pin-line" variant="info" />
              </div>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-shield-check-line" />
                  Trạng thái Pháp lý
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Hợp đồng:</span>
                    <Badge variant="neutral" className="bg-emerald-500/20 text-emerald-400 border-none text-[9px]  font-bold">Active</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">BOQ Version:</span>
                    <span className="text-xs font-bold text-os-primary">v1.2 (Latest)</span>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-800">
                  <Alert variant="info" className="bg-blue-900/30 border-blue-800 text-blue-200">
                    Phạm vi thi công đã được đồng bộ với BOQ mới nhất ngày 20/04/2026.
                  </Alert>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'items' && (
          <Card className="overflow-hidden border-none shadow-sm">
            <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">Chi tiết hạng mục thi công ({items.length})</h3>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" icon="ri-filter-3-line">Lọc hạng mục</Button>
                <AuthButton module="construction" action="update" variant="outline" size="sm" icon="ri-add-line">Thêm hạng mục</AuthButton>
              </div>
            </div>
            <Table
              columns={[
                { key: 'category', title: 'Phân loại' },
                { key: 'name', title: 'Hạng mục thi công' },
                { key: 'location', title: 'Khu vực/Giai đoạn' },
                { key: 'quantity', title: 'Khối lượng', align: 'right' },
                { key: 'spec', title: 'Tiêu chuẩn nghiệm thu' },
              ]}
              data={items}
              renderCell={(item, key) => {
                switch (key) {
                  case 'category':
                    return <Badge variant="neutral" className="bg-slate-100 text-slate-500 text-[9px]  font-bold">{item.work_category}</Badge>;
                  case 'name':
                    return (
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900">{item.work_item_name}</span>
                        <span className="text-[10px] text-slate-400 line-clamp-1 italic">{item.description}</span>
                      </div>
                    );
                  case 'location':
                    return (
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">{item.zone?.name || 'Chưa gán khu vực'}</span>
                        <span className="text-[10px] text-slate-400">{item.phase?.name || 'Chưa gán giai đoạn'}</span>
                      </div>
                    );
                  case 'quantity':
                    return (
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-slate-900">{item.planned_quantity}</span>
                        <span className="text-[10px] text-slate-400 font-bold ">{item.unit}</span>
                      </div>
                    );
                  case 'spec':
                    return (
                      <div className="max-w-[250px]">
                        <p className="text-[10px] text-slate-500 line-clamp-2">{item.acceptance_criteria || 'Theo spec bản vẽ thiết kế.'}</p>
                      </div>
                    );
                  default:
                    return null;
                }
              }}
            />
          </Card>
        )}

        {(activeTab === 'zones' || activeTab === 'boq' || activeTab === 'documents' || activeTab === 'versions') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được liên kết từ các phân hệ BOQ, Quản lý tài liệu và Cấu hình dự án."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
